/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/components/modular-form/utils/validation.ts
 * Release: 1.2.0 - Aggiunta validazione custom per partita IVA
 * Descrizione: Utility per validazione form modulare
 */

import type { FormFieldType } from '../types';
import { validateCodiceFiscale, validators } from '../../forms/utils/validators';
import { checkVAT, germany, france, spain, unitedKingdom, austria, belgium, netherlands, sweden, denmark, finland, ireland, portugal, greece, poland, czechRepublic, hungary, romania, bulgaria, croatia, slovenia, lithuania, latvia, estonia, luxembourg, malta, cyprus, norway, switzerland } from 'jsvat';

// Mappa paesi per jsvat (aggiornata con tutti i paesi supportati)
const countryMap: { [key: string]: any } = {
  'DE': germany,
  'FR': france,
  'ES': spain,
  'GB': unitedKingdom,
  'AT': austria,
  'BE': belgium,
  'NL': netherlands,
  'SE': sweden,
  'DK': denmark,
  'FI': finland,
  'IE': ireland,
  'PT': portugal,
  'EL': greece, // Grecia (codice alternativo)
  'GR': greece, // Grecia (codice standard)
  'PL': poland,
  'CZ': czechRepublic,
  'HU': hungary,
  'RO': romania,
  'BG': bulgaria,
  'HR': croatia,
  'SI': slovenia,
  'LT': lithuania,
  'LV': latvia,
  'EE': estonia,
  'LU': luxembourg,
  'MT': malta,
  'CY': cyprus,
  'NO': norway,
  'CH': switzerland
};

// Paesi EU/SEE/UK/CH/NO supportati da jsvat
const EU_VAT_COUNTRIES = [
  'AT','BE','BG','CY','CZ','DE','DK','EE','EL','ES','FI','FR','GB','GR','HR','HU','IE','IT','LT','LU','LV','MT','NL','PL','PT','RO','SE','SI','SK','XI','NO','CH','IS','LI'
];

// Validazione partita IVA per paesi non supportati da jsvat
const validateNonEUVAT = (countryCode: string, value: string): string | undefined => {
  const cleaned = value.replace(/\D/g, '');
  
  // Validazioni specifiche per paese
  switch (countryCode) {
    case 'IT': // Italia - gestita separatamente
      return undefined;
      
    case 'US': // Stati Uniti - EIN (9 cifre)
      if (cleaned.length !== 9) {
        return 'EIN deve essere di 9 cifre';
      }
      break;
      
    case 'CA': // Canada - BN (9 cifre)
      if (cleaned.length !== 9) {
        return 'BN deve essere di 9 cifre';
      }
      break;
      
    case 'AU': // Australia - ABN (11 cifre)
      if (cleaned.length !== 11) {
        return 'ABN deve essere di 11 cifre';
      }
      break;
      
    case 'JP': // Giappone - Corporate Number (13 cifre)
      if (cleaned.length !== 13) {
        return 'Corporate Number deve essere di 13 cifre';
      }
      break;
      
    case 'CN': // Cina - Tax Registration Number (15-18 cifre)
      if (cleaned.length < 15 || cleaned.length > 18) {
        return 'Tax Registration Number deve essere tra 15 e 18 cifre';
      }
      break;
      
    case 'IN': // India - GSTIN (15 cifre)
      if (cleaned.length !== 15) {
        return 'GSTIN deve essere di 15 cifre';
      }
      break;
      
    case 'BR': // Brasile - CNPJ (14 cifre)
      if (cleaned.length !== 14) {
        return 'CNPJ deve essere di 14 cifre';
      }
      break;
      
    case 'MX': // Messico - RFC (10-13 caratteri alfanumerici)
      const rfcCleaned = value.replace(/\s/g, '').toUpperCase();
      if (rfcCleaned.length < 10 || rfcCleaned.length > 13) {
        return 'RFC deve essere tra 10 e 13 caratteri';
      }
      if (!/^[A-Z0-9]+$/.test(rfcCleaned)) {
        return 'RFC deve contenere solo lettere e numeri';
      }
      break;
      
    default:
      // Validazione generica per altri paesi
      if (cleaned.length < 8 || cleaned.length > 20) {
        return 'Partita IVA deve essere tra 8 e 20 caratteri';
      }
      if (!/^[0-9]+$/.test(cleaned)) {
        return 'Partita IVA deve contenere solo numeri';
      }
  }
  
  return undefined;
};

export const validateField = (field: FormFieldType, value: any, fieldId?: string, allValues?: Record<string, any>): string | undefined => {
  // Gestione speciale per checkbox: se required=true, deve essere checked (value=true)
  if (field.required) {
    if (field.type === 'checkbox') {
      if (value !== true) {
        return 'Campo obbligatorio';
      }
    } else if (field.type === 'custom' && field.customComponent === 'AddressSection') {
      // Per indirizzi: considera valido se via E (città O cap) sono compilati
      if (!value || typeof value !== 'object') {
        return 'Campo obbligatorio';
      }
      const addr = value as { via?: string; citta?: string; cap?: string; search?: string };
      // Se c'è un search compilato (da autocomplete), è valido
      if (addr.search && addr.search.trim()) {
        // OK - indirizzo selezionato da autocomplete
      } else if (!addr.via?.trim() || (!addr.citta?.trim() && !addr.cap?.trim())) {
        // Richiede almeno via + (città o cap)
        return 'Campo obbligatorio';
      }
    } else {
      // Per altri tipi di campo: controllo standard
      if (!value || value === '') {
        return 'Campo obbligatorio';
      }
    }
  }

  if (!value || value === '') {
    return undefined;
  }

  // Validazione lunghezza
  if (field.validation?.minLength && value.length < field.validation.minLength) {
    return `Minimo ${field.validation.minLength} caratteri`;
  }

  if (field.validation?.maxLength && value.length > field.validation.maxLength) {
    return `Massimo ${field.validation.maxLength} caratteri`;
  }

  // Validazione pattern
  if (field.validation?.pattern) {
    const regex = new RegExp(field.validation.pattern);
    if (!regex.test(value)) {
      return field.validation.message || 'Formato non valido';
    }
  }

  // Validazione condizionale per codice fiscale
  if (fieldId === 'codiceFiscale' && allValues) {
    const countryCode = allValues.prefissoNazione;
    if (countryCode) {
      if (countryCode === 'IT') { // Italia
        // Usa il validatore completo per l'Italia
        const error = validateCodiceFiscale(value);
        if (error) {
          return error;
        }
      } else {
        // Validazione per altri paesi
        const cleaned = value.replace(/\s/g, '').toUpperCase();
        if (cleaned.length < 8 || cleaned.length > 20) {
          return 'Codice fiscale deve essere tra 8 e 20 caratteri';
        }
        if (!/^[A-Z0-9]+$/.test(cleaned)) {
          return 'Codice fiscale deve contenere solo lettere e numeri';
        }
      }
    }
  }

  // Validazione condizionale per partita IVA
  if (fieldId === 'partitaIva' && allValues) {
    const countryCode = allValues.prefissoNazione;
    
    if (countryCode) {
      if (countryCode === 'IT') { // Italia
        // Usa il validatore completo per l'Italia
        const error = validators.partitaIva(value);
        if (error) {
          return error;
        }
      } else if (EU_VAT_COUNTRIES.includes(countryCode)) {
        // Usa jsvat per validazione formale dei paesi EU/SEE/UK/CH/NO
        const country = countryMap[countryCode];
        if (country) {
          // Aggiungi il codice paese al valore per la validazione jsvat
          const vatWithCountry = countryCode + value;
          const result = checkVAT(vatWithCountry, [country]);
          if (!result.isValid) {
            return `Partita IVA non valida per ${countryCode}. Verifica il formato e le cifre di controllo.`;
          }
        } else {
          // Paese EU ma non supportato da jsvat - usa validazione generica
          const error = validateNonEUVAT(countryCode, value);
          if (error) {
            return error;
          }
        }
      } else {
        // Validazione per paesi non EU
        const error = validateNonEUVAT(countryCode, value);
        if (error) {
          return error;
        }
      }
    } else {
      // Se non c'è paese, usa validazione generica
      const error = validateNonEUVAT('', value);
      if (error) {
        return error;
      }
    }
  }

  // Validazione email per tutti i campi email
  if (field.type === 'email' && value) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return 'Email non valida';
    }
  }

  // Validazione URL per tutti i campi URL
  if (field.type === 'url' && value) {
    try {
      new URL(value);
    } catch {
      return 'URL non valido';
    }
  }

  // Validazione URL
  if (field.validation?.url) {
    try {
      new URL(value);
    } catch {
      return 'URL non valido';
    }
  }

  // Validazione condizionale per Codice Univoco e PEC (fatturazione elettronica)
  if ((fieldId === 'codiceUnivoco' || fieldId === 'pec') && allValues) {
    const countryCode = allValues.prefissoNazione;
    const codiceUnivoco = allValues.codiceUnivoco || '';
    const pec = allValues.pec || '';
    
    if (countryCode === 'IT') { // Italia
      // Se è il campo codice univoco e ha un valore, validalo
      if (fieldId === 'codiceUnivoco' && value) {
        const error = validators.codiceUnivoco(value);
        if (error) {
          return error;
        }
      }
      
      // Se è il campo PEC e ha un valore, validalo
      if (fieldId === 'pec' && value) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return 'PEC non valida';
        }
      }
      
      // Controlla che almeno uno dei due campi sia compilato
      if (codiceUnivoco.trim() === '' && pec.trim() === '') {
        if (fieldId === 'codiceUnivoco') {
          return 'Inserisci almeno il Codice Univoco o la PEC per la fatturazione elettronica';
        }
        if (fieldId === 'pec') {
          return 'Inserisci almeno il Codice Univoco o la PEC per la fatturazione elettronica';
        }
      }
    } else {
      // Per altri paesi, valida solo se i campi sono compilati
      if (fieldId === 'codiceUnivoco' && value) {
        const error = validators.codiceUnivoco(value);
        if (error) {
          return error;
        }
      }
      
      if (fieldId === 'pec' && value) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return 'PEC non valida';
        }
      }
    }
  }

  // Validazione custom
  if (field.validation?.custom) {
    const customValidator = field.validation.custom;
    
    // Se è una stringa, cerca nel registry dei validatori
    if (typeof customValidator === 'string') {
      const validator = validators[customValidator as keyof typeof validators];
      if (validator) {
        const error = validator(value);
        if (typeof error === 'string') {
          return error;
        }
      }
    }
    
    // Se è una funzione, chiamala direttamente
    if (typeof customValidator === 'function') {
      const error = customValidator(value);
      if (typeof error === 'string') {
        return error;
      }
    }
  }

  return undefined;
};

export const validateForm = (fields: { id: string, field: FormFieldType }[], values: Record<string, any>): Record<string, string> => {
  const errors: Record<string, string> = {};
  fields.forEach(({ id, field }) => {
    const error = validateField(field, values[id] || '', id, values);
    if (error) {
      errors[id] = error;
    }
  });
  return errors;
};

export const isFormValid = (errors: Record<string, string>): boolean => {
  return Object.keys(errors).length === 0;
}; 